import {
  ISessionData,
  ISessionVehicle,
  IVehicle,
  IVehicleId,
  sessionVehicleSchema,
  TPrString,
  vehicleSchema,
} from 'data/schema';
import { IPublicSession, sharedSessionSchema, userSession } from 'state/session';
import { isObject } from 'utils/objects';
import { isEqual } from 'utils/deepEqual';
import { showConfirmDialog } from 'state/confirm-dialog';
import { t } from '@lingui/core/macro';
import { tenantConfig } from 'utils/tenant';
import { identityMatches, identityMatchesAll, IDENTITY_FIELDS, IVehicleIdentity } from 'utils/vehicles';
import { getComparisonVehicle } from './compare';
import type { DeckAnnouncement } from './deckSelection';
import { heldDeckModelRevision } from './deckPrStringHold';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import logger from 'utils/log';

/**
 * This vehicle schema is used when resetting session data from vehicle registry
 * Vehicles in the registry may not have all the fields present in the session
 * such as dgaLink or vinOrMarketingCode, which may be undefined
 * This schema transformation ensures that missing fields are explicitly set to undefined
 */
const vehicleSchemaWithUndefined = sessionVehicleSchema.transform((vehicle) => {
  const result = { ...vehicle } as Record<string, unknown>;

  // Iterate through schema shape to find optional fields that are missing
  const schemaShape = sessionVehicleSchema.shape as Record<keyof ISessionVehicle, unknown>;

  for (const key of Object.keys(schemaShape)) {
    // Set undefined for any field that doesn't exist in the vehicle data
    if (!(key in vehicle)) {
      result[key] = undefined;
    }
  }

  return result as ISessionVehicle;
});

/**
 * Helper function to determine the next comparison mode when removing vehicles
 */
function getNextComparisonMode(vehiclesCount: number, removedActiveOrStashed: boolean): ISessionData['comparisonMode'] {
  if (vehiclesCount === 0) return 'off';
  const currentMode = userSession().get('comparisonMode');
  if (removedActiveOrStashed && currentMode !== 'off') return 'selecting';
  return currentMode;
}

/**
 * Helper function to reset all active vehicle fields
 */
function resetActiveVehicle() {
  userSession().setValues({
    ...vehicleSchemaWithUndefined.parse({}),
    activeVehicleId: undefined,
  });
}

/** Hard AWS API Gateway `PostToConnection` frame cap (not a raisable quota). */
export const SESSION_BROADCAST_CAP_BYTES = 131_072;
/** Prune target: keeps the broadcast frame with headroom under the hard cap. */
export const SESSION_BROADCAST_BUDGET_BYTES = 106_496;

/**
 * Measures the UTF-8 byte size of the session document as it is actually broadcast:
 * projected through `sharedSessionSchema`, then serialized. Never use `.length` here —
 * that counts UTF-16 code units, not the bytes the wire (and the AWS cap) measure.
 *
 * Uses `safeParse`, not `parse` (AVS-4382): `state/session.ts`'s `updatePublicSessionNow` treats a
 * failed projection through this same schema as reachable and non-fatal ("gracefully fail to
 * update the session if some of the data is not there") rather than throwing. This function follows
 * the same shape, adapted to returning a number instead of performing a write. On failure we cannot
 * tell the real size, so we log it and return 0 — this makes the current prune pass a no-op instead
 * of either evicting every unprotected vehicle over a transient/unrelated parse failure, or silently
 * permitting unbounded growth from a `measure()` that always reports "over budget".
 */
export function measureBroadcastBytes(session: IPublicSession): number {
  const result = sharedSessionSchema.safeParse(session);

  if (!result.success) {
    logger.warn('[VehicleRegistry] Failed to measure broadcast size: session did not match sharedSessionSchema.');
    return 0;
  }

  const json = JSON.stringify(result.data);
  return new TextEncoder().encode(json).length;
}

/**
 * Evicts the oldest unprotected vehicles (by `addedAt`) from `vehicles` until the broadcast
 * document fits `SESSION_BROADCAST_BUDGET_BYTES`. The active vehicle and the `comparing: true`
 * vehicle are never evicted, and neither is `additionalProtectedId` when supplied — used by
 * `addVehicle` to protect the vehicle it is in the middle of adding, which has no `activeVehicleId`
 * / `comparing` protection of its own yet (AVS-4382: without this, a vehicle could be evicted by
 * the very prune its own addition triggered, and `addVehicle` would return an id that is not in
 * the registry). If the protected vehicles alone exceed the budget, the loop stops (it cannot spin
 * and cannot evict a protected entry) and the condition is logged.
 *
 * Runs on every `addVehicle`, including a dealer session with no customer connected yet:
 * `SESSION_BROADCAST_CAP_BYTES` is API Gateway's `PostToConnection` frame cap, which applies
 * whenever this session's document IS pushed over the wire — session start, a later reconnect,
 * or the moment a customer joins a session the dealer built up solo. Pruning continuously as
 * vehicles are added keeps the persisted document broadcast-ready for that moment, instead of
 * silently failing (or truncating) the first broadcast after a customer finally joins.
 */
function pruneVehiclesForBroadcastBudget(
  vehicles: Record<string, IVehicle>,
  additionalProtectedId?: string
): Record<string, IVehicle> {
  const activeVehicleId = userSession().get('activeVehicleId');
  const [comparisonVehicleId] = getComparisonVehicle();
  const protectedIds = new Set<string>(
    [activeVehicleId, comparisonVehicleId, additionalProtectedId].filter((id): id is string => Boolean(id))
  );

  let candidateVehicles = vehicles;
  const measure = () => measureBroadcastBytes({ ...userSession().get(), vehicles: candidateVehicles });

  while (measure() > SESSION_BROADCAST_BUDGET_BYTES) {
    const unprotectedEntries = Object.entries(candidateVehicles).filter(([id]) => !protectedIds.has(id));

    if (unprotectedEntries.length === 0) {
      const measuredSize = measure();
      logger.error(
        `[VehicleRegistry] Protected vehicles alone measure ${measuredSize} B, over the ${SESSION_BROADCAST_BUDGET_BYTES} B broadcast budget and close to the ${SESSION_BROADCAST_CAP_BYTES} B hard cap. Shipping the session document oversized as-is — the broadcast to peers will fail with 413.`
      );
      break;
    }

    const [oldestId] = unprotectedEntries.reduce((oldest, entry) =>
      entry[1].addedAt < oldest[1].addedAt ? entry : oldest
    );
    const { [oldestId]: _evictedVehicle, ...remainingVehicles } = candidateVehicles;
    candidateVehicles = remainingVehicles;
  }

  return candidateVehicles;
}

/**
 * Duplicate-path fields that self-heal on re-announce (AVS-4400). Excludes identity/media
 * fields (prString, vinOrMarketingCode, okapiCode, carImages, etc.) — merging those blindly
 * risks reproducing AVS-4248.
 */
const MERGEABLE_DUPLICATE_FIELDS: readonly (keyof IVehicle)[] = [
  'wltpText',
  'carDisplayName',
  'carDetails',
  'efficiencyClass',
  // AVS-4481: only applies when the incoming value has content, so it upgrades '' to MP1/MP2 and never clears.
  'modelRevision',
];

/**
 * True when `value` carries real content: not undefined, not null, not the empty string, and
 * not an empty plain object (an empty `carDetails: {}` on a re-announce is not real content and
 * must not heal over an existing populated value).
 */
function hasContent(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).length > 0;
  }
  return true;
}

/** Copies `source[key]` onto `target[key]`, preserving the per-key value type. */
function copyField<K extends keyof IVehicle>(target: Partial<IVehicle>, source: IVehicle, key: K): void {
  target[key] = source[key];
}

/**
 * Regulatory consumption fields. An announce may legitimately say a car HAS no such value, and for
 * these fields only that has to be able to overwrite a wrong one — see
 * {@link isExplicitRegulatoryClear}.
 */
const REGULATORY_FIELDS: readonly (keyof IVehicle)[] = ['wltpText', 'efficiencyClass'];

/**
 * True when the incoming announce explicitly states a regulatory field is empty for this car.
 *
 * `hasContent` alone made corruption one-way (AVS-4400 review): `showCar` normalizes a car with no
 * WLTP to `wltpText: ''`, so once another car's numbers had been misapplied, re-announcing the
 * correct — and genuinely empty — car could never heal them back off. Only an empty STRING counts,
 * and only on `raw` (what the caller actually passed, before schema parsing): `undefined` still
 * means "not supplied", because the 3-arg `showCar(prString, name, wltpText)` overload DCC uses
 * carries no `efficiencyClass` at all and must not wipe one a richer announce established.
 */
function isExplicitRegulatoryClear(field: keyof IVehicle, raw: Partial<IVehicle>): boolean {
  return REGULATORY_FIELDS.includes(field) && field in raw && raw[field] === '';
}

/**
 * Returns the subset of `MERGEABLE_DUPLICATE_FIELDS` where `incoming` differs from `existing`,
 * or `undefined` when nothing would change (AVS-4400). Skips healing when both sides have a
 * present, differing `prString` — that means the match came from vinOrMarketingCode/okapiCode
 * alone, so `existing` and `incoming` may describe different configurations of the same car.
 *
 * `raw` is the caller's own partial, kept alongside the parsed `incoming` purely so an explicitly
 * empty regulatory field can be told apart from an absent one.
 */
function healDuplicateFields(
  existingId: IVehicleId,
  vehicles: Record<string, IVehicle>,
  incoming: IVehicle,
  raw: Partial<IVehicle>
): Partial<IVehicle> | undefined {
  const existing = vehicles[existingId];
  if (
    hasContent(existing.prString) &&
    hasContent(incoming.prString) &&
    !isEqual(existing.prString, incoming.prString)
  ) {
    return undefined;
  }

  const healedFields: Partial<IVehicle> = {};
  let hasChanges = false;

  for (const field of MERGEABLE_DUPLICATE_FIELDS) {
    const clearsExistingValue = isExplicitRegulatoryClear(field, raw) && hasContent(existing[field]);
    if ((clearsExistingValue || hasContent(incoming[field])) && !isEqual(incoming[field], existing[field])) {
      copyField(healedFields, incoming, field);
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return undefined;
  }

  const borrowed = findBorrowedField(existingId, vehicles, healedFields);
  if (borrowed) {
    logger.warn(
      `[VehicleRegistry] Refused duplicate self-heal for "${existing.carDisplayName}": ${borrowed.field} ` +
        `${truncateForLog(borrowed.value)} belongs to a different registry entry ("${borrowed.owner.carDisplayName}"). ` +
        `The announcing host sent a configuration mixing two cars (AVS-4413).`
    );
    return undefined;
  }

  return healedFields;
}

/** Heal fields whose value, found on another carline's entry, proves it was borrowed from it. */
const BORROW_CHECKED_FIELDS = ['carDisplayName', 'wltpText'] as const;

/**
 * The first heal value that belongs to a DIFFERENT carline's registry entry (AVS-4413).
 *
 * DCC can flip its active configuration field by field, so a re-announce may carry the matched
 * entry's prString with another car's name or WLTP. Healing that would label one car with
 * another's data. Only a differing, non-empty `carDisplayName` proves a different carline:
 * colour siblings share a name and often a WLTP, and a real correction between them must heal.
 */
function findBorrowedField(
  targetId: IVehicleId,
  vehicles: Record<string, IVehicle>,
  healedFields: Partial<IVehicle>
): { field: (typeof BORROW_CHECKED_FIELDS)[number]; value: string; owner: IVehicle } | undefined {
  const targetName = vehicles[targetId].carDisplayName;
  if (!hasContent(targetName)) {
    return undefined;
  }

  for (const field of BORROW_CHECKED_FIELDS) {
    const value = healedFields[field];
    if (!value) {
      continue;
    }
    const owner = Object.entries(vehicles).find(
      ([id, vehicle]) =>
        id !== targetId &&
        hasContent(vehicle.carDisplayName) &&
        vehicle.carDisplayName !== targetName &&
        vehicle[field] === value
    )?.[1];
    if (owner) {
      return { field, value, owner };
    }
  }
  return undefined;
}

/**
 * Whether {@link activateVehicle} would actually do something for `id`. Extracted so `addVehicle`
 * can derive its `emit` from it: `activateVehicle` is the only thing that republishes an entry to
 * the session root and emits, and it returns early without writing anything when the entry is
 * missing or comparison mode is 'on' (AVS-4400 review).
 */
function canActivateVehicle(id: IVehicleId, vehicles: Record<string, IVehicle> | undefined): boolean {
  return !!vehicles?.[id] && userSession().get('comparisonMode') !== 'on';
}

/**
 * Add vehicle to the registry. Optionally (and by default) activate it immediately
 *
 * @param vehicle - Partial vehicle data to add
 * @param makeActive - Whether to activate the vehicle after adding (default: true)
 * @returns Vehicle ID (either newly created or existing if duplicate found)
 * @throws {Error} If vehicle data is invalid and cannot be parsed
 *
 * On a duplicate match, self-heals `MERGEABLE_DUPLICATE_FIELDS` onto the existing entry (AVS-4400).
 */
export function addVehicle(vehicle: Partial<IVehicle>, makeActive: boolean = true): IVehicleId {
  let newVehicle: IVehicle;

  try {
    newVehicle = vehicleSchema.parse(vehicle);
  } catch (error) {
    logger.error('[VehicleRegistry] Failed to parse vehicle data:', error);
    showToast({
      title: t`Failed to add vehicle`,
      message: t`The vehicle data is invalid.`,
      type: 'error',
      notificationId: 'ERROR_VEHICLE_ADD',
    });
    throw error;
  }

  // Check if a vehicle with the same prString, vinOrMarketingCode, or okapiCode already exists
  const existingId = (['prString', 'vinOrMarketingCode', 'okapiCode'] as const).reduce<IVehicleId | undefined>(
    (found, key) => found || (newVehicle[key] && findVehicleId(key, newVehicle[key])),
    undefined
  );

  if (existingId) {
    // Self-heal changed MERGEABLE_DUPLICATE_FIELDS onto the existing entry (AVS-4400).
    const vehicles = userSession().get('vehicles');
    const existingVehicle = vehicles?.[existingId];
    const healedFields =
      vehicles && existingVehicle ? healDuplicateFields(existingId, vehicles, newVehicle, vehicle) : undefined;
    // Deriving this from `makeActive` alone was wrong (AVS-4400 review): activateVehicle() bails
    // out without writing anything when comparison mode is 'on', so a re-announce carrying
    // corrected WLTP healed the registry, left the customer-visible root on the stale value, and
    // suppressed the only emission that would have synced or re-rendered either.
    const willActivate = makeActive && canActivateVehicle(existingId, vehicles);

    if (existingVehicle && healedFields) {
      // Every MERGEABLE_DUPLICATE_FIELD is part of sessionVehicleSchema, so when activation will
      // not republish the entry we carry the heal to the root ourselves rather than let root and
      // registry drift apart.
      const healActiveRoot = !willActivate && existingId === userSession().get('activeVehicleId') ? healedFields : {};
      userSession().setValues(
        { vehicles: { ...vehicles, [existingId]: { ...existingVehicle, ...healedFields } }, ...healActiveRoot },
        { emit: !willActivate } // do not emit if activateVehicle() below will emit for us
      );
    }

    if (makeActive) {
      activateVehicle(existingId);
    }
    return existingId; // Return early without adding the duplicate vehicle
  }

  // add new vehicle
  const newId = crypto.randomUUID();
  // Protect newId for this prune: it has no activeVehicleId/comparing protection of its own
  // until the setValues/activateVehicle calls below run, so without this it could be evicted
  // by the very prune its own addition triggers (AVS-4382).
  const updatedVehicles = pruneVehiclesForBroadcastBudget(
    { ...userSession().get('vehicles'), [newId]: newVehicle },
    newId
  );

  userSession().setValues(
    { vehicles: updatedVehicles },
    // Same reasoning as the duplicate branch above: with comparison mode 'on', activateVehicle()
    // writes and emits nothing, so `!makeActive` would add the vehicle to the registry silently.
    { emit: !(makeActive && canActivateVehicle(newId, updatedVehicles)) }
  );

  if (makeActive) {
    activateVehicle(newId);
  }

  return newId;
}

/**
 * Set a vehicle active using the ID of an existing vehicle in the vehicles registry.
 * When a vehicle is active, it's going to be the one that's used in the stream
 *
 * @param id - The vehicle ID to activate
 * @returns true if activation succeeded, false otherwise
 */
export function activateVehicle(id: IVehicleId): boolean {
  const vehicles = userSession().get('vehicles');

  // Check if the vehicle exists in the registry. Also do not activate if already comparing
  if (!canActivateVehicle(id, vehicles)) {
    return false;
  }

  const vehicleData = vehicleSchemaWithUndefined.parse(vehicles[id]);

  // Determine carImagesStatus based on available image sources
  // If the vehicle has VTP photos but no car images, set status to 'ready' so 2D gallery can display them
  const hasVtpPhotos = vehicleData.vtpPhotos && vehicleData.vtpPhotos.length > 0;
  const hasCarImages = vehicleData.carImages?.items && vehicleData.carImages.items.length > 0;
  const carImagesStatus = hasVtpPhotos && !hasCarImages ? 'ready' : vehicleData.carImagesStatus;

  // Apply the vehicle's data to the session root level
  userSession().setValues({
    ...vehicleData,
    activeVehicleId: id,
    streamActive: true,
    endedAt: undefined,
    ...(carImagesStatus !== vehicleData.carImagesStatus ? { carImagesStatus } : {}),
  });

  return true;
}

/**
 * Swaps the active vehicle with a comparing vehicle.
 *
 * Behavior:
 * - Stashes the currently active vehicle (sets comparing: true)
 * - Activates the target vehicle (sets as activeVehicleId)
 * - Only works when NOT in 'on' comparison mode (enforced by activateVehicle)
 *
 * @param nextVehicleId - ID of the vehicle to activate
 *
 * @example
 * // Swap from vehicle1 to vehicle2
 * swapComparedVehicles(vehicle2Id);
 */
export function swapComparedVehicles(nextVehicleId: IVehicleId) {
  const session = userSession().get();
  const currentActiveVehicleId = session.activeVehicleId;

  if (!currentActiveVehicleId || !nextVehicleId) {
    return;
  }

  const currentActiveVehicle = userSession().currentVehicle;

  if (!currentActiveVehicle) {
    return;
  }

  const nextVehicle = session.vehicles?.[nextVehicleId];

  if (!nextVehicle) {
    return;
  }

  stashVehicle(currentActiveVehicleId, false);

  activateVehicle(nextVehicleId);
}

/**
 * Marks a vehicle for comparison by setting its comparing flag to true.
 * Automatically disables the comparing flag on all other vehicles.
 *
 * @param targetVehicleId - ID of the vehicle to mark for comparison
 * @param emit - Whether to emit state change event (default: true)
 *
 * @example
 * // Stash vehicle2 for comparison
 * stashVehicle(vehicle2Id);
 */
export function stashVehicle(targetVehicleId: IVehicleId, emit: boolean = true) {
  const vehicles = userSession().get('vehicles');
  if (!vehicles || !targetVehicleId || !vehicles[targetVehicleId]) {
    return;
  }
  const myVehicle = vehicles[targetVehicleId];
  // Set comparing flag on the active vehicle - stash it
  const updatedVehicles = {
    ...disableComparingForVehicles(false),
    [targetVehicleId]: {
      ...myVehicle,
      comparing: true,
    },
  };

  userSession().setValues({ vehicles: updatedVehicles }, { emit });
}

/**
 * Sets comparing: false on all vehicles and returns the updated vehicles object.
 * @returns The updated vehicles registry with comparing set to false for all vehicles, or an empty object if no vehicles exist.
 */
export function disableComparingForVehicles(emit: boolean = true): Record<string, IVehicle> {
  const vehicles = userSession().get('vehicles');
  // Set comparing: false on all vehicles
  if (!vehicles) {
    return {};
  }
  const updatedVehicles: Record<string, IVehicle> = {};
  for (const vehicleId of Object.keys(vehicles || {})) {
    updatedVehicles[vehicleId] = {
      ...vehicles[vehicleId],
      comparing: false,
    };
  }
  userSession().setValues({ vehicles: updatedVehicles }, { emit });
  return updatedVehicles;
}

/**
 * Remove vehicle from the comparison candidates
 */
export async function deactivateVehicleComparison(deactivatedVehicleId: IVehicleId) {
  const vehicles = userSession().get('vehicles');
  const stashedVehicle = vehicles ? vehicles[deactivatedVehicleId] : undefined;

  if (!vehicles || !stashedVehicle) {
    return;
  }

  const result = await showConfirmDialog({
    dialogId: 'DIALOG_VEHICLE_DISCARD',
    title: t`Discard vehicle from compare session`,
    message: `Are you sure you want to discard ${stashedVehicle.carDisplayName}  from your compare session?`,
    confirmText: t`Discard vehicle`,
    cancelText: t`Keep both vehicles`,
    'data-testid': 'deactivate-vehicle-comparison-dialog',
  });

  if (!result) {
    // user cancelled the action
    return;
  }

  // 1. Clear all comparing flags so no vehicle is marked as stashed
  // 2. If there is another vehicle set for comparison, that vehicle becomes active

  const comparisonVehicleIds = getComparingVehicleIds();
  const remainingVehicleId = comparisonVehicleIds.find((id) => id && id !== deactivatedVehicleId);

  // Clear all comparing flags — the remaining vehicle should be a normal active vehicle, not stashed
  disableComparingForVehicles(false);

  // Always reset to 'off' after discarding — all comparing flags are cleared above,
  // so the 'selecting' invariant (one vehicle has comparing:true) cannot hold. (AVS-4179)
  // Emit explicitly because activateVehicle may be a no-op if the remaining vehicle is already active.
  userSession().setValues({ comparisonMode: 'off' });

  if (remainingVehicleId) {
    activateVehicle(remainingVehicleId);
  } else {
    // No vehicles left, reset all vehicle fields
    resetActiveVehicle();
  }
}

// get the vehicles we have in comparison (active + stashed)
export function getComparingVehicleIds() {
  const [stashedVehicleId] = getComparisonVehicle();
  const activeVehicleId = userSession().get('activeVehicleId');

  const comparisonVehicleIds = [activeVehicleId, stashedVehicleId].filter(Boolean);
  return [...new Set(comparisonVehicleIds)];
}

export function getComparingVehicles() {
  const ids = getComparingVehicleIds();
  const vehicles = userSession().get('vehicles');

  return ids.reduce(
    (acc, id) => {
      if (id && vehicles?.[id]) {
        acc[id] = vehicles[id];
      }
      return acc;
    },
    {} as Record<IVehicleId, IVehicle>
  );
}

export function isVehicleStashed(vehicle?: IVehicle): boolean {
  const comparisonVehicle = getComparisonVehicle();
  const stashed = comparisonVehicle ? comparisonVehicle[1] : undefined;

  if (!stashed || !vehicle || !isObject(vehicle) || !isObject(stashed)) return false;

  return identityMatches(stashed, vehicle);
}

/**
 * Checks if the stashed comparison vehicle and the active vehicle are the same.
 * Vehicles are considered the same if prString, vinOrMarketingCode, or okapiCode match.
 *
 * @returns true if the vehicles match, false otherwise
 *
 * @example
 * if (areComparedVehiclesEqual()) {
 *   showToast({ message: 'Cannot compare a vehicle with itself' });
 * }
 */
export function areComparedVehiclesEqual(): boolean {
  const vehicles = userSession().get('vehicles');
  const activeVehicleId = userSession().get('activeVehicleId');
  const activeVehicle = activeVehicleId ? vehicles?.[activeVehicleId] : undefined;
  const [, stashedVehicle] = getComparisonVehicle();

  if (!activeVehicle || !stashedVehicle) return false;

  return identityMatches(activeVehicle, stashedVehicle);
}

/**
 * Find vehicle by any property and return its id in the vehicles registry
 */
export function findVehicleId<K extends keyof IVehicle>(key: K, value: IVehicle[K]): IVehicleId | undefined {
  const vehicles = userSession().get('vehicles');

  if (!vehicles) {
    return undefined;
  }

  // Find the first vehicle that matches the key-value pair
  for (const [vehicleId, vehicleData] of Object.entries(vehicles)) {
    if ((vehicleData as IVehicle)[key] === value) {
      return vehicleId;
    }
  }

  return undefined;
}

/**
 * Remove a vehicle from the registry by its ID
 */
export function removeVehicle(removalVehicleId: IVehicleId): boolean {
  const vehicles = userSession().get('vehicles');

  // Check if the vehicle exists in the registry
  if (!vehicles || !vehicles[removalVehicleId]) {
    return false;
  }

  const wasActiveVehicle = userSession().get('activeVehicleId') === removalVehicleId;

  // Create a new vehicles object without the removed vehicle
  const updatedVehicles = Object.entries(vehicles).reduce(
    (acc, [vehicleId, vehicleData]) => {
      if (vehicleId !== removalVehicleId) {
        acc[vehicleId] = vehicleData as IVehicle;
      }
      return acc;
    },
    {} as Record<string, IVehicle>
  );

  const [stashedVehicleId] = getComparisonVehicle();

  // Determine next comparison mode
  const removedActiveOrStashed = wasActiveVehicle || stashedVehicleId === removalVehicleId;
  const nextComparisonMode = getNextComparisonMode(Object.keys(updatedVehicles).length, removedActiveOrStashed);

  // Update the vehicles registry
  userSession().setValues({ vehicles: updatedVehicles, comparisonMode: nextComparisonMode });

  // If we removed the active vehicle, try to activate the most recent one
  if (wasActiveVehicle) {
    const recentVehicles = getRecentVehicles(1);
    if (recentVehicles.length > 0) {
      activateVehicle(recentVehicles[0].id);
    } else {
      // No vehicles left, reset all vehicle fields
      resetActiveVehicle();
    }
  }

  return true;
}

/**
 * Get the most recently added vehicles from the registry
 * @param session - The session state instance
 * @param count - Number of vehicles to return (default: 2)
 * @returns Array of vehicles with their IDs, sorted by addedAt timestamp (most recent first)
 */
export function getRecentVehicles(count: number = 2): Array<{ id: IVehicleId; vehicle: IVehicle }> {
  const vehicles = userSession().get('vehicles');

  if (!vehicles || Object.keys(vehicles).length === 0) {
    return [];
  }

  // Convert to array, sort by addedAt descending, and take the first 'count' items
  return Object.entries(vehicles)
    .map(([id, vehicleData]) => ({ id, vehicle: vehicleData as IVehicle }))
    .sort((a, b) => b.vehicle.addedAt - a.vehicle.addedAt)
    .slice(0, count);
}

/**
 * Options passed to {@link updateVehicle} that bind a write to the vehicle it was computed for.
 */
export interface IUpdateVehicleOptions {
  /** Reject the write (with a console warning) when the target entry's identity does not match. */
  expect?: IVehicleIdentity;
}

/**
 * Truncates a prString for a console warning. Production prStrings run ~1 KB and must not be
 * dumped whole into the console.
 */
export function truncateForLog(value: string | undefined, maxLength: number = 40): string {
  if (!value) {
    return 'none';
  }
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

/**
 * The registry id whose entry satisfies `expected` under the strict (AND) correlation rule, or
 * `undefined` when the expected car is not in the registry.
 */
function findVehicleIdByIdentity(
  expected: IVehicleIdentity,
  vehicles: Record<string, IVehicle>
): IVehicleId | undefined {
  return Object.keys(vehicles).find((id) => identityMatchesAll(expected, vehicles[id]));
}

/**
 * Vehicle related updates that will update the active car and the record in the registry.
 *
 * The target vehicle can be given two ways: pass a vehicle id (string) to target it explicitly,
 * or pass `{ expect }` with the identity the update was computed for — if the active vehicle's
 * identity doesn't match, the update is redirected to the vehicle it WAS computed for when that
 * one is still in the registry, and rejected with a warning when it is not, instead of silently
 * corrupting the wrong car (AVS-4400).
 *
 * @param updates - Partial vehicle fields to merge onto the target entry
 * @param idOrOptions - Either the vehicle id to update (defaults to the active vehicle when
 * omitted), or an options object whose `expect` correlates this update with the vehicle it was
 * computed for.
 * @returns `true` when the update was written (to the active vehicle, an explicit id, or the
 * `expect` vehicle it was redirected to), `false` when it was dropped — no such vehicle, or an
 * `expect` naming a car the registry does not hold.
 *
 * @example
 * // Bind the update to the car it was computed for — redirected to that car, or rejected,
 * // instead of corrupting a different vehicle if the car changed underneath the caller.
 * E3D.vehicles.updateVehicle({ wltpText: newWltpText }, { expect: { prString } });
 */
export function updateVehicle(updates: Partial<IVehicle>, idOrOptions?: IVehicleId | IUpdateVehicleOptions): boolean {
  const vehicles = userSession().get('vehicles');
  const id = typeof idOrOptions === 'string' ? idOrOptions : undefined;
  const options = typeof idOrOptions === 'string' ? undefined : idOrOptions;
  const activeVehicleId = userSession().get('activeVehicleId');
  let targetId = id ?? activeVehicleId;

  if (!vehicles || !targetId || !vehicles[targetId]) {
    return false;
  }

  const expected = options?.expect;

  // Strict AND over the identifiers the caller actually supplied (AVS-4400 review). The OR rule
  // `identityMatches` uses to FIND a car is unsafe as a guard: a configuration switch keeps the
  // VIN and okapiCode, so `{ prString: staleConfig, okapiCode: sameCar }` would match on the
  // okapiCode alone and write the stale configuration's regulatory data.
  if (expected && !identityMatchesAll(expected, vehicles[targetId])) {
    const mismatchedVehicle = vehicles[targetId];
    // Same notion of "supplied" the matcher uses, so the warning can never name a field the guard
    // did not actually compare.
    const mismatches =
      IDENTITY_FIELDS.filter((field) => !!expected[field])
        .map(
          (field) =>
            `expected ${field} ${truncateForLog(expected[field])} did not match target's ${truncateForLog(mismatchedVehicle[field])}`
        )
        .join('; ') || 'the expected identity carried no usable identifier';

    // `expect` names a real car, and it is usually still in the registry — dropping the write
    // loses data that was correct for THAT vehicle, which is the same defect class as writing it
    // to the wrong one. Redirect when we can find it; reject only when we cannot.
    const correlatedId = findVehicleIdByIdentity(expected, vehicles);

    if (!correlatedId) {
      logger.warn(
        `[VehicleRegistry] Rejected updateVehicle for "${mismatchedVehicle.carDisplayName || targetId}": ${mismatches}.`
      );
      return false;
    }

    logger.warn(
      `[VehicleRegistry] Redirected updateVehicle away from "${mismatchedVehicle.carDisplayName || targetId}" to the vehicle it was computed for: ${mismatches}.`
    );
    targetId = correlatedId;
  }

  const targetVehicle = vehicles[targetId];

  if (id === undefined && !expected) {
    const regulatoryFieldsInUpdate = REGULATORY_FIELDS.filter((field) => field in updates);
    if (regulatoryFieldsInUpdate.length > 0) {
      logger.warn(
        `[VehicleRegistry] Uncorrelated update to ${regulatoryFieldsInUpdate.join(', ')} on "${targetVehicle.carDisplayName || targetId}": pass options.expect or an id to correlate it (AVS-4400).`
      );
    }
  }

  // Merge base is the resolved target entry, not the active vehicle (AVS-4400).
  const parsedUpdates = vehicleSchema.parse({ ...targetVehicle, ...updates });

  // `addedAt`/`comparing` are registry-entry-only fields (schema.ts: "this extra field should
  // not be copied to the session data") — activateVehicle() already respects that by parsing
  // through sessionVehicleSchema; do the same here so a root-level update doesn't add keys
  // IPublicSession doesn't have, which trips syncToServer's "no relevant keys changed" branch
  // and fires a needless full-document emitOnly broadcast on every update (AVS-4400).
  const { addedAt: _addedAt, comparing: _comparing, ...sessionSafeUpdates } = parsedUpdates;
  const activeUpdates = targetId === activeVehicleId ? sessionSafeUpdates : {};
  userSession().setValues({
    vehicles: {
      ...vehicles,
      [targetId]: parsedUpdates,
    },
    ...activeUpdates,
  });

  return true;
}

/**
 * Whether a 3D Showroom announcement names the car this tab already holds. The server strips the
 * root `prString` from broadcasts, so after another tab's car change only the active vehicle entry
 * names the new configuration (AVS-4469).
 */
export function isCurrentConfiguration(prString: TPrString): boolean {
  return prString === userSession().get('prString') || prString === userSession().currentVehicle?.prString;
}

type TGalleryFields = Pick<IVehicle, 'carImages' | 'carImagesStatus'>;

const RETIRED_GALLERY: TGalleryFields = { carImages: undefined, carImagesStatus: 'unset' };

/**
 * The gallery root or the active entry holds for exactly `prString` (root's first), or `undefined`
 * when neither does. DCC supplies its own images and can push them before it announces the
 * configuration they belong to (AVS-4399), so a gallery for another car never comes along.
 */
function galleryForConfiguration(prString: TPrString): TGalleryFields | undefined {
  const session = userSession();
  const carImages = session.get('carImages');
  if (carImages?.prString === prString) {
    return { carImages, carImagesStatus: session.get('carImagesStatus') };
  }
  const activeEntry = session.currentVehicle;
  if (activeEntry?.carImages?.prString === prString) {
    return { carImages: activeEntry.carImages, carImagesStatus: activeEntry.carImagesStatus };
  }
  return undefined;
}

/**
 * The car fields root holds for `prString`, to carry into an active entry that already names it.
 * A stale root `prString` means a broadcast delivered this car. If a pending local edit kept
 * `vehicles` dirty, that broadcast set root's car fields but skipped the registry, and
 * `updateVehicle` would spread the older entry back over root. Root's fields win (AVS-4469).
 */
function broadcastCarFields(prString: TPrString): Partial<IVehicle> {
  const carDisplayName = userSession().get('carDisplayName');
  return { ...(carDisplayName ? { carDisplayName } : {}), ...(galleryForConfiguration(prString) ?? RETIRED_GALLERY) };
}

/** Adopt a standalone Showroom announcement of the car this tab already holds (`isCurrentConfiguration`). */
export function adoptCurrentConfiguration(prString: TPrString): void {
  if (prString === userSession().get('prString')) {
    updateVehicle({ carStatus: 'ready' });
    return;
  }
  updateVehicle({ prString, carStatus: 'ready', ...broadcastCarFields(prString) });
}

/**
 * Whether an announcement may activate a car: an active car, a running stream, not ended.
 * `activateVehicle` sets `streamActive` and clears `endedAt`, so it would reopen a session that is
 * over (AVS-4422).
 */
function isLiveSession(): boolean {
  const session = userSession();
  return !!session.get('activeVehicleId') && !!session.get('streamActive') && !session.get('endedAt');
}

/**
 * Activate the saved car a 3D Showroom announcement names, when it is not the active one (AVS-4422).
 *
 * Right after a car switch the deck can announce the PREVIOUS car (a start-up echo). Writing that
 * onto the active entry left the new car's entry holding the old configuration under the new car's
 * name. Keyed on the active ENTRY, not root `prString`: a follower's root can be stale because the
 * server strips it from broadcasts (AVS-4469).
 *
 * @returns `true` when another entry was activated; `false` when the caller keeps its usual path:
 * the active entry already holds the configuration, no other entry does, comparison mode is on, or
 * the session is not live (`activateVehicle` sets `streamActive` and clears `endedAt`, so it would
 * reopen an ended session).
 */
export function activateAnnouncedConfiguration(prString: TPrString): boolean {
  const session = userSession();
  const activeVehicleId = session.get('activeVehicleId');
  const vehicles = session.get('vehicles');

  if (!activeVehicleId || !isLiveSession() || !vehicles) {
    return false;
  }
  if (vehicles[activeVehicleId]?.prString === prString) {
    return false;
  }

  const [latestHolder] = Object.keys(vehicles)
    .filter((id) => id !== activeVehicleId && vehicles[id].prString === prString)
    .sort((a, b) => vehicles[b].addedAt - vehicles[a].addedAt);

  if (!latestHolder || !activateVehicle(latestHolder)) {
    return false;
  }

  // The deck just announced this car, so it is loaded: an entry saved as 'unset' would otherwise
  // hide the stream (Embedded). Written directly, not through updateVehicle, which would spread the
  // entry back over root and undo activateVehicle's root-only VTP gallery status.
  if (session.get('carStatus') !== 'ready') {
    const activated = session.get('vehicles')?.[latestHolder];
    session.setValues({
      ...(activated
        ? { vehicles: { ...session.get('vehicles'), [latestHolder]: { ...activated, carStatus: 'ready' } } }
        : {}),
      carStatus: 'ready',
    });
  }
  return true;
}

/**
 * Handle a 3D Showroom announcement no saved car holds on a live session (AVS-4422).
 *
 * The deck can announce a car the registry no longer holds: its entry was removed or pruned for the
 * broadcast budget, or the deck lost track after an error or a remount. Writing that onto the active
 * entry kept the active car's name, VIN and WLTP under another configuration (DCC) or overwrote the
 * saved car (standalone).
 *
 * It becomes its own active entry named "Configured car" when it is an `echo`, a car this tab's deck
 * replaced, or when this tab is a customer's, which applies it locally and never writes it (AVS-4412).
 * The new entry takes a gallery only when it is for this configuration. Through `addVehicle`, so budget
 * pruning applies. On a dealer tab any other announcement, `undefined` included, is another client's
 * pick: that client saves the entry and its broadcast brings it here. A rival entry for the same car
 * raced its write of `vehicles`, so the registry and root are left alone.
 *
 * @returns `true` when the announcement is handled here, whether or not an entry was added; `false`
 * when the caller keeps its usual path: the session is not live, it has no active entry, comparison
 * mode is on (`addVehicle` adds without activating then, the same rule `activateAnnouncedConfiguration`
 * follows), or a saved car holds the configuration (`addVehicle`'s duplicate path would rename it).
 */
export function registerAnnouncedConfiguration(prString: TPrString, announcement?: DeckAnnouncement): boolean {
  const session = userSession();
  if (
    !isLiveSession() ||
    !session.currentVehicle ||
    session.get('comparisonMode') === 'on' ||
    findVehicleId('prString', prString)
  ) {
    return false;
  }

  if (announcement !== 'echo' && user.isDealer) {
    logger.info(
      `[VehicleRegistry] The 3D Showroom announced ${truncateForLog(prString)}, which no saved car holds and this tab did not write: another client's pick, awaiting its broadcast (AVS-4422).`
    );
    return true;
  }

  addVehicle(
    {
      prString,
      // AVS-4481: a new configuration from inside the deck keeps the model, so the revision carries over.
      // During an AVS-4462 hold, the mounted deck is still configuring the held car, not `currentVehicle`
      // (the broadcast-derived root car), so resolve the revision from the same held pr-string/registry
      // pairing `heldDeckModelRevision` uses.
      modelRevision: heldDeckModelRevision(
        { prString: session.get('prString'), modelRevision: session.currentVehicle?.modelRevision },
        session.get('vehicles')
      ),
      carDisplayName: t`Configured car`,
      carStatus: 'ready',
      ...galleryForConfiguration(prString),
    },
    true
  );
  return true;
}

/**
 * Apply a configuration announced by the 3D Showroom on the DCC / web-component path (AVS-4399).
 *
 * DCC drives 3DE as a web component and every other piece of session state reaches us over the
 * broadcast, but `prString` does not: the server strips it from broadcasts unless `updateEverything`
 * is set, so an announcement has to be written locally here.
 *
 * Three things have to hold at once, and each of them broke a different way before:
 *
 *  - root and the registry must agree. A root-only write left the active vehicle entry on the
 *    previous configuration, and since `updateVehicle` rebases off that entry and writes the whole
 *    rebased block back to root, the next unrelated vehicle update silently reverted root
 *    `prString` along with carDisplayName, vinOrMarketingCode, carDetails, wltpText and vtpPhotos.
 *  - the gallery must not outlive the configuration it belongs to. `updateVehicle` spreads the
 *    merged ENTRY over root, so naming only `prString` republished the previous car's `carImages`
 *    under the new car, `carImagesStatus: 'ready'` included. Retire the gallery in the same write
 *    unless one for this exact configuration has already arrived.
 *  - a push that lands before `showCar` has registered anything must still be kept. `updateVehicle`
 *    silently no-ops without an active vehicle ENTRY, so that case falls back to a root write.
 *    Gate on the entry rather than on `activeVehicleId`: the id can be set while `vehicles[id]` is
 *    absent (a broadcast or read-repair applying `vehicles: {}` while a locally-dirty id is held
 *    back), and checking only the id would take a branch that no-ops with no fallback.
 *
 * A configuration another saved car already holds activates that car first (AVS-4422), ahead of
 * the root-equality check: a follower's stale root can name it while the active entry does not.
 * On a live session, one no saved car holds goes to `registerAnnouncedConfiguration` (AVS-4422).
 */
export function applyAnnouncedPrString(prString: TPrString | undefined, announcement?: DeckAnnouncement): void {
  if (!prString || activateAnnouncedConfiguration(prString)) {
    return;
  }
  if (prString === userSession().get('prString')) {
    return;
  }

  const vehicle = userSession().currentVehicle;
  if (vehicle?.prString === prString) {
    updateVehicle({ prString, ...broadcastCarFields(prString) });
    return;
  }
  if (registerAnnouncedConfiguration(prString, announcement)) {
    return;
  }
  // DCC supplies its own images and can push them before it announces the configuration they
  // belong to, so only retire a gallery that is actually for a different car.
  const retireGallery = galleryForConfiguration(prString) ? {} : RETIRED_GALLERY;

  if (vehicle) {
    updateVehicle({ prString, ...retireGallery });
    return;
  }
  userSession().setValues({ prString, ...retireGallery });
}

/**
 * Apply a configuration announced by the 3D Showroom on the standalone path (AVS-4422).
 *
 * Unlike the DCC / web-component path, 3DE owns the car here, so a configuration nothing else claims
 * resets the car attributes that described the previous one. An `announcement` of `undefined` counts
 * as a non-echo, as in `registerAnnouncedConfiguration`.
 */
export function applyStandaloneAnnouncedPrString(
  prString: TPrString | undefined,
  announcement?: DeckAnnouncement
): void {
  if (userSession().get('carSelectionMethod') === 'dcc') {
    userSession().setValues({ carStatus: 'ready' });
  }
  if (!prString) return;

  // AVS-4422: right after a switch the deck can announce the PREVIOUS car. A car the
  // registry already holds is activated, never written onto the active entry.
  if (activateAnnouncedConfiguration(prString)) return;

  // AVS-4248: The 3D stream fires update:pr-string on every (re-)mount,
  // including no-op re-announces of the existing configuration. Resetting
  // vinOrMarketingCode/dgaLink/carImages on a same-prString event wipes
  // identifiers GMS brands (VW, N) need to refetch 2D images, leaving the
  // next 2D toggle with a blank gallery. A car another tab changed arrives by broadcast
  // without its root prString; it is not a new car either (AVS-4469).
  if (isCurrentConfiguration(prString)) {
    adoptCurrentConfiguration(prString);
    return;
  }

  // AVS-4422: on a live session a configuration no saved car holds is registered (an echo, or on a
  // customer tab) or left to its picker's broadcast; the reset below would overwrite the active entry.
  if (registerAnnouncedConfiguration(prString, announcement)) return;

  console.info(
    'New PR string received from 3D Showroom. Updating session PR string and resetting session car attributes.'
  );

  // prString actually changed: the old VIN/dgaLink/carImages are stale.
  updateVehicle({
    prString,
    vinOrMarketingCode: '',
    dgaLink: '',
    carStatus: 'ready',
    carImages: undefined,
    carImagesStatus: 'unset',
    carDetails: undefined,
    carDisplayName: t`Configured car`,
  });
}

export function setComparingMode(mode: ISessionData['comparisonMode']) {
  const session = userSession().get();
  const activeVehicleId = session.activeVehicleId;
  if (!activeVehicleId) {
    return;
  }

  userSession().setValues({
    comparisonMode: mode,
  });
}

/**
 * Comparison mode transition map
 */
const COMPARISON_TRANSITIONS: Record<ISessionData['comparisonMode'], ISessionData['comparisonMode']> = {
  off: 'selecting',
  selecting: 'on',
  on: 'off',
};

export async function toggleComparingMode() {
  if (!tenantConfig().isAllowed('comparison')) {
    return;
  }
  const currentMode = userSession().get('comparisonMode');
  setComparingMode(COMPARISON_TRANSITIONS[currentMode]);
  return userSession().get('comparisonMode');
}
